/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { useState } from 'react';
import classnames from 'classnames';
import { reaction } from 'mobx';
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import {
    //AddToCartComponent,
    //AddToOrderTemplateComponent,
    IPriceComponentResources,
    ISwatchItem,
    ProductComponentSwatchComponent,
    RatingComponent,
    IOrderTemplateNameDialogResources,
    IAddToOrderTemplateDialogResources,
    IItemAddedToOrderTemplateDialogResources,
    IDuplicateItemsWhenAddingToOrderTemplateDialogResources
} from '@msdyn365-commerce/components';
import {
    getUrlSync,
    IAny,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGeneric,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import {
    AttributeSwatch,
    AttributeValue,
    Customer,
    ProductList,
    ProductPrice,
    ProductSearchResult,
    SimpleProduct,
    UnitOfMeasureConversion
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    Dictionary,
    DimensionTypes,
    generateImageUrl,
    getProductPageUrlSync,
    IDimensionsApp,
    StringExtensions,
    OrderTemplate,
    IProductInventoryInformation
} from '@msdyn365-commerce-modules/retail-actions';
import {
    format,
    Tooltip,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    Node,
    onTelemetryClick,
    UncontrolledTooltip
} from '@msdyn365-commerce-modules/utilities';
import { ICartState } from '@msdyn365-commerce/global-state';
import PQueue from 'p-queue';
import { ProductSearchResultExt, SimpleProductExt } from '../../../../shared-utils/types';
import { AddToCartComponent, IAddToCartToastResources } from '../../../../components/common/add-to-cart.component';
import AddToOrderTemplateComponent from './addtoordertemplate.component';
import { toast } from '../../../../components/toast/toast';
import { humanizeUnitOfMeasure } from '../../../../components/unit-of-measure';
import DiscountBadge from '../../../../components/discount-badge/discount-badge';
import { IProductListByProductId } from '../../../../actions/get-product-lists-by-product-ids';
import QuantityInput from '../../../../components/common/quantity-input';
import PriceComponent from './price.component';
import { IAppSettingsInContext } from '../../../../settings/app.settings.autogenerated';
// import { ensureBaseImageUrl } from '../../shared-utils/image-utilities';

export interface IProductComponentProps
    extends IComponentProps<{
        cart?: ICartState;
        product?: ProductSearchResultExt;
        simpleProduct?: SimpleProduct;
        orderTemplates?: OrderTemplate[];
        customerInformation?: Customer;
        unitConversions?: UnitOfMeasureConversion[];
    }> {
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    pricePerText?: string;
    priceNotLoggedInText?: string;
    cartQueue?: PQueue;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    wishlistButton?: React.ReactNode;
    isEnabledProductDescription?: boolean;
    isPriceMinMaxEnabled?: boolean;
    disableBuyButton?: boolean;
    showPrice?: boolean;
    showAgreementProduct?: boolean;
    showAvailability?: boolean;
    userHasAccess?: boolean;
    buyAlwaysAdd?: boolean;
    allowZeroQuantity?: boolean;
    priceResources?: IPriceComponentResources;
    inventoryLabel?: string;
    inventoryStatus?: IProductInventoryInformation;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
    addToCartText?: string;
    updateCartText?: string;
    removeFromCartText?: string;
    outOfStockText?: string;
    priceErrorText?: string;
    priceText?: string;
    netPriceText?: string;
    discountText?: string;
    toastStrings?: IAddToCartToastResources;
    inCartTooltipText?: string;
    transitTooltip?: string;
    agreementProductTooltip?: string;
    addToOrderTemplateResources?: IAddToOrderTemplateResources;
    signInLinkText?: string;
    subTotalLabel?: string;
    goToCartButtonLabel?: string;
    moreProductsLabel?: string;
    readMoreText?: string;
    assemblyInstructionsText?: string;
    onUpdateOrderTemplates?: (product?: ProductSearchResultExt, orderTemplate?: ProductList) => Promise<OrderTemplate[]>;
    retrieveOrderTemplates?(): Promise<OrderTemplate[]>;
}

export interface IProductComponent extends IComponent<IProductComponentProps> {}

export interface IAddToOrderTemplateResources {
    addToOrderTemplateHeader: string;
    noOrderTemplatesMessage: string;
    noOrderTemplatesDescription: string;
    createAnOrderTemplateButtonText: string;
    createNewOrderTemplateButtonText: string;
    cancelOrderTemplateCreationButtonText: string;
    selectTemplatesText: string;
    addToTemplateButtonText: string;
    lineItemsText: string;
    createOrderTemplateHeader: string;
    orderTemplateTitle: string;
    orderTemplateNameAriaLabel: string;
    createOrderTemplateDescription: string;
    defaultOrderTemplateName: string;
    createOrderTemplateButtonText: string;
    cancelNewOrderTemplateCreationButtonText: string;
    viewOrderTemplateButtonText: string;
    continueShoppingButtonText: string;
    itemAddedToOrderTemplateHeaderItemOneText: string;
    itemAddedToOrderTemplateHeaderItemFormatText: string;
    itemAddedToOrderTemplateHeaderMessageText: string;
    priceFree: string;
    originalPriceText: string;
    currentPriceText: string;
    duplicatedProductsHeader: string;
    duplicatedProductsDescription: string;
    updateQuantityButtonText: string;
    cancelDuplicateItemsButtonText: string;
    addToOrderTemplateButtonText: string;
    addToOrderTemplateButtonTooltip: string;
}

const PriceComponentActions = {};

/**
 * Gets the product page url from the default swatch selected.
 * @param  productData - Product card to be rendered.
 * @returns The default color swatch selected if any.
 */
function getDefaultSwatchSelected(coreContext: ICoreContext, productData?: ProductSearchResult): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const attributeSwatches = productData.AttributeValues.find(
        attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === dimensionToPreSelectInProductCard
    )?.Swatches;

    if (!ArrayExtensions.hasElements(attributeSwatches)) {
        return null;
    }

    const defaultSwatch = attributeSwatches.find(item => item.IsDefault === true) ?? attributeSwatches[0];
    return defaultSwatch;
}

/**
 * Gets the product image from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductImageUrlFromDefaultSwatch(coreContext: ICoreContext, productData?: ProductSearchResult): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    const swatchProductImageUrls = defaultSwatch?.ProductImageUrls;
    if (!ArrayExtensions.hasElements(swatchProductImageUrls)) {
        return productData?.PrimaryImageUrl;
    }

    return generateImageUrl(swatchProductImageUrls[0], coreContext.request.apiSettings);
}

/**
 * Updates the product url link to product details page.
 * @param  productDetailsPageUrl - Product page url.
 * @param  coreContext - Context of the module using the component.
 * @param  queryString - Querystring to be added to the URL.
 * @returns The update product page url.
 */
function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
    const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
    if (sourceUrl.search) {
        sourceUrl.search += `&${queryString}`;
    } else {
        sourceUrl.search += queryString;
    }

    const updatedUrl = new URL(sourceUrl.href);
    return updatedUrl.pathname + sourceUrl.search;
}

/**
 * Gets the react node for product availability.
 * @param inventoryAvailabilityLabel - The product information.
 * @returns The node representing markup for product availability.
 */
function renderProductAvailability(
    context: ICoreContext,
    showAvailability: boolean,
    inventoryAvailabilityLabel: string | undefined,
    inventoryStatus?: IProductInventoryInformation
): JSX.Element | null {
    let availabilityClassName: 'retrieving' | 'available' | 'not-available' | 'error' = 'retrieving';

    if (!showAvailability) {
        return null;
    }

    if (inventoryStatus?.IsProductAvailable || inventoryStatus?.StockLevelLabel?.startsWith('!')) {
        availabilityClassName = 'available';
    } else if (inventoryStatus?.IsProductAvailable === false) {
        availabilityClassName = 'not-available';
    }

    if (inventoryStatus?.StockLevelCode === 'ERR') {
        availabilityClassName = 'error';
    }

    const stockLevelLabel = inventoryStatus?.StockLevelLabel?.startsWith('!')
        ? inventoryStatus?.StockLevelLabel?.substring(1)
        : inventoryStatus?.StockLevelLabel;
    const inventoryStatusText = inventoryStatus?.StockLevelCode === 'ERR'
        ? stockLevelLabel || 'ERROR'
        : inventoryStatus
            ? inventoryAvailabilityLabel?.substring(inventoryAvailabilityLabel.startsWith('!') ? 1 : 0)
            : <>&nbsp;</>;

    return (
        <div className={classnames('msc-product__availability', availabilityClassName)}>
            <div>
                {typeof inventoryStatusText === 'string'
                    ? format(inventoryStatusText, inventoryStatus?.ProductAvailableQuantity.AvailableQuantity)
                    : inventoryStatusText}
            </div>
        </div>
    );
}

/**
 * Gets the product page url from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param productUrl - Product page url for the product card.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductPageUrlFromDefaultSwatch(
    coreContext: ICoreContext,
    productUrl: string,
    productData?: ProductSearchResult
): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    if (!defaultSwatch?.SwatchValue) {
        return productUrl;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const queryStringEncoded = encodeURIComponent(defaultSwatch.SwatchValue);
    const queryString = `${dimensionToPreSelectInProductCard}=${queryStringEncoded}`;
    return updateProductUrl(productUrl, coreContext, queryString);
}

function generateAddToOrderTemplateDialogResources(
    resources: IAddToOrderTemplateResources | undefined
): IAddToOrderTemplateDialogResources {
    return {
        addToOrderTemplateHeader: resources?.addToOrderTemplateHeader || 'addToOrderTemplateHeader',

        noOrderTemplatesMessage: resources?.noOrderTemplatesMessage || 'noOrderTemplatesMessage',
        noOrderTemplatesDescription: resources?.noOrderTemplatesDescription || 'noOrderTemplatesDescription',

        createAnOrderTemplateButtonText: resources?.createAnOrderTemplateButtonText || 'createAnOrderTemplateButtonText',
        createNewOrderTemplateButtonText: resources?.createNewOrderTemplateButtonText || 'createNewOrderTemplateButtonText',
        cancelOrderTemplateCreationButtonText: resources?.cancelOrderTemplateCreationButtonText || 'cancelOrderTemplateCreationButtonText',

        selectTemplatesText: resources?.selectTemplatesText || 'selectTemplatesText',
        addToTemplateButtonText: resources?.addToTemplateButtonText || 'addToTemplateButtonText',
        lineItemsText: resources?.lineItemsText || 'lineItemsText'
    };
}

function generateOrderTemplateNameDialogResources(resources: IAddToOrderTemplateResources | undefined): IOrderTemplateNameDialogResources {
    return {
        orderTemplateHeaderLabel: resources?.createOrderTemplateHeader || 'createOrderTemplateHeader',
        orderTemplateTitleLabel: resources?.orderTemplateTitle || 'orderTemplateTitle',
        orderTemplateNameAriaLabel: resources?.orderTemplateNameAriaLabel || 'orderTemplateNameAriaLabel',
        orderTemplateDescription: resources?.createOrderTemplateDescription || 'createOrderTemplateDescription',

        defaultOrderTemplateName: resources?.defaultOrderTemplateName || 'defaultOrderTemplateName',

        orderTemplateButtonLabel: resources?.createOrderTemplateButtonText || 'createOrderTemplateButtonText',
        orderTemplateCancelButtonLabel: resources?.cancelNewOrderTemplateCreationButtonText || 'cancelNewOrderTemplateCreationButtonText'
    };
}

function generateItemAddedToOrderTemplateDialogResources(
    resources: IAddToOrderTemplateResources | undefined
): IItemAddedToOrderTemplateDialogResources {
    return {
        viewOrderTemplateButtonText: resources?.viewOrderTemplateButtonText || 'viewOrderTemplateButtonText',
        continueShoppingButtonText: resources?.continueShoppingButtonText || 'continueShoppingButtonText',
        itemAddedToOrderTemplateHeaderItemOneText:
            resources?.itemAddedToOrderTemplateHeaderItemOneText || 'itemAddedToOrderTemplateHeaderItemOneText',
        itemAddedToOrderTemplateHeaderItemFormatText:
            resources?.itemAddedToOrderTemplateHeaderItemFormatText || 'itemAddedToOrderTemplateHeaderItemFormatText',
        itemAddedToOrderTemplateHeaderMessageText:
            resources?.itemAddedToOrderTemplateHeaderMessageText || 'itemAddedToOrderTemplateHeaderMessageText',
        freePriceText: resources?.priceFree || 'priceFree',
        originalPriceText: resources?.originalPriceText || 'originalPriceText',
        currentPriceText: resources?.currentPriceText || 'currentPriceText'
    };
}

function generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(
    resources: IAddToOrderTemplateResources | undefined
): IDuplicateItemsWhenAddingToOrderTemplateDialogResources {
    return {
        duplicatedProductsHeader: resources?.duplicatedProductsHeader || 'duplicatedProductsHeader',
        duplicatedProductsDescription: resources?.duplicatedProductsDescription || 'duplicatedProductsDescription',
        updateQuantityButtonText: resources?.updateQuantityButtonText || 'updateQuantityButtonText',
        cancelDuplicateItemsButtonText: resources?.cancelDuplicateItemsButtonText || 'cancelDuplicateItemsButtonText'
    };
}

const ProductCard: React.FC<IProductComponentProps> = ({
    data,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    currentPriceText,
    ratingAriaLabel,
    ratingCountAriaLabel,
    pricePerText,
    priceNotLoggedInText,
    cartQueue,
    allowBack,
    typeName,
    id,
    telemetryContent,
    quickViewButton,
    productComparisonButton,
    wishlistButton,
    isEnabledProductDescription,
    isPriceMinMaxEnabled,
    disableBuyButton,
    showPrice = true,
    showAgreementProduct,
    showAvailability = true,
    buyAlwaysAdd,
    allowZeroQuantity,
    priceResources,
    inventoryLabel,
    inventoryStatus,
    dimensionAvailabilities,
    swatchItemAriaLabel,
    addToCartText,
    updateCartText,
    removeFromCartText,
    outOfStockText,
    priceErrorText,
    priceText,
    netPriceText,
    discountText,
    toastStrings,
    inCartTooltipText,
    transitTooltip,
    agreementProductTooltip,
    addToOrderTemplateResources,
    signInLinkText,
    subTotalLabel,
    goToCartButtonLabel,
    moreProductsLabel,
    readMoreText,
    assemblyInstructionsText,
    onUpdateOrderTemplates,
    retrieveOrderTemplates
}) => {
    const { product, simpleProduct } = data;
    const cartQuantity = buyAlwaysAdd ? 0 : data.cart?.cart?.CartLines?.find(x => x.ProductId === product?.RecordId)?.Quantity ?? 0;
    const [orderQuantity, setOrderQuantity] = useState(cartQuantity);
    const [breakoff] = useState(false);
    const orderTemplateIndicatorRef = React.createRef<HTMLDivElement>();

    let productUrl = getProductPageUrlSync(product?.Name ?? '', product?.RecordId ?? Number.MIN_VALUE, context.actionContext, undefined);
    if (allowBack) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }
    const productImageUrlFromSwatch = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl;
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    const [productPageUrl, setProductPageUrl] = useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = useState<string | undefined>(productImageUrlFromSwatch);
    const [showCartCountTooltip, setShowCartCountTooltip] = useState<boolean>(false);

    // @ts-expect-error -- Variable is declared but its value is never read.
    const [forceUpdate, setForceUpdate] = useState<number>(0);

    React.useEffect(() => {
        if (buyAlwaysAdd) {
            return;
        }

        const disposer = reaction(
            () => data.cart?.cart.CartLines,
            () => {
                if (cartQuantity > 0 && !data.cart?.cart.CartLines?.find(x => x.ProductId === product?.RecordId)) {
                    // This product was removed from cart, reset quantity
                    setOrderQuantity(0);
                }
            }
        );

        return disposer;
    }, []);

    React.useEffect(() => {
        setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImageUrlFromSwatch);
    }, [productUrl, productPageUrlFromSwatch, productImageUrlFromSwatch]);

    React.useEffect(() => {
        // Set 'title' attribute on AddToOrderTemplate buttons
        document.querySelectorAll('.order-template-button').forEach(element => {
            element.setAttribute('title', element.getAttribute('aria-label') || '');
        });
    }, []);
    const [selectedSwatchItems] = useState(new Dictionary<DimensionTypes, ISwatchItem>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    const enableStockCheck = context.app.config.enableStockCheck;

    const siteContext = context as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    /**
     * Updates the product page and Image url based on swatch selected.
     * @param coreContext - Context of the caller.
     * @param swatchItem - Dimension swatch selected.
     */
    const updatePageAndImageUrl = React.useCallback(
        (coreContext: ICoreContext, swatchItem: ISwatchItem) => {
            const dimensionType = swatchItem.dimensionType;
            selectedSwatchItems.setValue(dimensionType, swatchItem);
            if (StringExtensions.isNullOrWhitespace(swatchItem.value)) {
                return;
            }
            const queryString = `${dimensionType}=${swatchItem.value}`;
            let productPageUrlWithSwatch = '';
            if (productPageUrl.includes(dimensionType)) {
                const newUrl = new URL(productPageUrl, coreContext.request.apiSettings.baseUrl);
                newUrl.searchParams.delete(dimensionType);
                productPageUrlWithSwatch = updateProductUrl(newUrl.toString(), context, queryString);
            } else {
                productPageUrlWithSwatch = updateProductUrl(productPageUrl, context, queryString);
            }
            setProductPageUrl(productPageUrlWithSwatch);
            if (dimensionType === dimensionToPreSelectInProductCard) {
                const swatchProductImageUrl = ArrayExtensions.hasElements(swatchItem.productImageUrls)
                    ? swatchItem.productImageUrls[0]
                    : undefined;
                const newImageUrl = generateImageUrl(swatchProductImageUrl, coreContext.request.apiSettings);
                setProductImageUrl(newImageUrl);
            }
        },
        [selectedSwatchItems, context, productPageUrl]
    );

    if (!product) {
        return null;
    }

    /**
     * Checks if rendering the particular dimensions is allowed for product card.
     * @param dimensionType - Dimension to be displayed.
     * @returns Updates the state with new product page url.
     */
    function shouldDisplayDimension(dimensionType: string): boolean {
        const dimensionsContext = context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = dimensionsContext.app.config.dimensionsInProductCard;
        return (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            dimensionsToDisplayOnProductCard.includes(dimensionType.toLocaleLowerCase() as DimensionTypes)
        );
    }

    /**
     * Gets the react node for product dimension as swatch.
     * @param  attributeValues - Attribute value property from product.
     * @returns The node representing markup for unit of measure component.
     */
    function renderProductDimensions(attributeValues?: AttributeValue[]): JSX.Element | null {
        if (!attributeValues) {
            return null;
        }

        return (
            <div className='msc-product__dimensions'>
                {attributeValues.map((item: AttributeValue) => {
                    const dimensionTypeValue = item.KeyName?.toLocaleLowerCase() ?? '';
                    if (!shouldDisplayDimension(dimensionTypeValue)) {
                        return null;
                    }

                    const siteContext = context as ICoreContext<IDimensionsApp>;
                    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
                    const dimensionType = dimensionTypeValue as DimensionTypes;
                    const swatches =
                        item.Swatches?.map<ISwatchItem>(swatchItem => {
                            return {
                                itemId: `${item.RecordId ?? ''}-${dimensionTypeValue}-${swatchItem.SwatchValue ?? ''}`,
                                value: swatchItem.SwatchValue ?? '',
                                dimensionType,
                                colorHexCode: swatchItem.SwatchColorHexCode,
                                imageUrl: swatchItem.SwatchImageUrl,
                                productImageUrls: swatchItem.ProductImageUrls,
                                isDefault: swatchItem.IsDefault,
                                swatchItemAriaLabel: swatchItemAriaLabel ? format(swatchItemAriaLabel, dimensionType) : '',
                                isDisabled:
                                    enableStockCheck &&
                                    dimensionAvailabilities?.find(
                                        dimensionAvailability => dimensionAvailability.value === (swatchItem.SwatchValue ?? '')
                                    )?.isDisabled
                            };
                        }) ?? [];
                    if (
                        dimensionType === dimensionToPreSelectInProductCard &&
                        ArrayExtensions.hasElements(swatches) &&
                        !swatches.some(swatch => swatch.isDefault)
                    ) {
                        swatches[0].isDefault = true;
                    }
                    return (
                        <ProductComponentSwatchComponent
                            context={context}
                            swatches={swatches}
                            onSelectDimension={updatePageAndImageUrl}
                            key={item.RecordId}
                        />
                    );
                })}
            </div>
        );
    }

    function renderOrderTemplateButton() {
        if (!data.customerInformation?.IsB2b) {
            return null;
        }

        return (
            <AddToOrderTemplateComponent
                className='order-template-button'
                addToOrderTemplateButtonText={
                    addToOrderTemplateResources?.addToOrderTemplateButtonText || 'addToOrderTemplateButtonText'
                }
                addToOrderTemplateButtonTooltip={
                    addToOrderTemplateResources?.addToOrderTemplateButtonTooltip || 'addToOrderTemplateButtonTooltip'
                }
                addToOrderTemplateDialogResources={generateAddToOrderTemplateDialogResources(addToOrderTemplateResources)}
                createOrderTemplateDialogResources={generateOrderTemplateNameDialogResources(addToOrderTemplateResources)}
                itemAddedToOrderTemplateDialogResources={generateItemAddedToOrderTemplateDialogResources(addToOrderTemplateResources)}
                duplicateItemsWhenAddingToOrderTemplateDialogResources={generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(
                    addToOrderTemplateResources
                )}
                context={context}
                id={id}
                typeName={typeName}
                data={{
                    product: {
                        RecordId: data.product?.RecordId!,
                        ItemId: data.product?.ItemId,
                        Name: data.product?.Name,
                        Description: data.product?.Description,
                        ProductTypeValue: data.product?.RecordId!,
                        DefaultUnitOfMeasure: data.product?.DefaultUnitOfMeasure,
                        BasePrice: data.product?.BasePrice!,
                        Price: data.product?.Price!,
                        AdjustedPrice: data.product?.Price!,
                        MasterProductId: data.product?.MasterProductId,
                        PrimaryImageUrl: data.product?.PrimaryImageUrl,
                        ExtensionProperties: data.product?.ExtensionProperties
                    },
                    quantity: orderQuantity || 1,
                    customerInformation: data.customerInformation,
                    partOfProductLists: data.product?.PartOfProductLists
                }}
                onSuccess={async orderTemplate => {
                    await onUpdateOrderTemplates?.(data.product, orderTemplate);
                    toast({
                        text: `${data.product?.Name}${addToOrderTemplateResources?.itemAddedToOrderTemplateHeaderMessageText}`,
                        autoClose: 3000,
                        canClose: true,
                        position: 'bottom-right',
                        type: 'success',
                        showProgress: true
                    });
                }}
                retrieveOrderTemplates={retrieveOrderTemplates}
            />
        );
    }

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- -- Do not need type check for appsettings
    // @ts-expect-error
    const isUnitOfMeasureEnabled = context.app.config && context.app.config.unitOfMeasureDisplayType === 'buyboxAndBrowse';
    const _renderCartButton = (renderCartCount: boolean): JSX.Element => {
        const product1: SimpleProduct = {
            RecordId: data.product?.RecordId!,
            ItemId: data.product?.ItemId,
            Name: data.product?.Name,
            Description: data.product?.Description,
            ProductTypeValue: data.product?.RecordId!,
            DefaultUnitOfMeasure: data.product?.DefaultUnitOfMeasure,
            BasePrice: data.product?.BasePrice!,
            Price: data.product?.Price!,
            AdjustedPrice: data.product?.Price!,
            MasterProductId: data.product?.MasterProductId,
            PrimaryImageUrl: data.product?.PrimaryImageUrl,
            ExtensionProperties: data.product?.ExtensionProperties
        };
        const productDimensionAvailabilities =
            dimensionAvailabilities &&
            dimensionAvailabilities?.find(
                dimensionAvailability =>
                    ArrayExtensions.hasElements(dimensionAvailabilities) &&
                    dimensionAvailability.masterProductId &&
                    dimensionAvailability.masterProductId === product.MasterProductId
            );

        let cartCount: JSX.Element | undefined;
        // const cartQuantity = data.cart?.cart.CartLines?.find(x => x.ProductId === product.RecordId)?.Quantity;
        const cartQuantity = data.cart?.cart.CartLines?.filter(x => x.ProductId === product.RecordId).reduce(
            (acc, val) => acc + (val.Quantity || 0),
            0
        );

        if (renderCartCount && cartQuantity) {
            const cartCountId = `product-component-cart-count-${product.RecordId}`;
            const tooltipString = inCartTooltipText || '{0} i handlekurven';
            const humanizedUOM = humanizeUnitOfMeasure(context, product.DefaultUnitOfMeasure).toLowerCase();
            const tooltipText = format(tooltipString, `${cartQuantity} ${humanizedUOM}`);

            cartCount = (
                <span
                    id={cartCountId}
                    className={`${baseClassName}__cart-count`}
                    onMouseOver={() => setShowCartCountTooltip(true)}
                    onMouseOut={() => setShowCartCountTooltip(false)}
                    onTouchStart={() => setShowCartCountTooltip(true)}
                >
                    {`${cartQuantity} ${humanizedUOM}`}
                    <Tooltip isOpen={showCartCountTooltip} target={`#${cartCountId}`} className='msc-cart-count-tooltip'>
                        {tooltipText}
                    </Tooltip>
                </span>
            );
        }

        return (
            <Node className='ms-search-result-container__add-to-cart-container'>
                <AddToCartComponent
                    addToCartText={addToCartText ? addToCartText : 'Legg i kurv'}
                    updateCartText={updateCartText ? updateCartText : 'Oppdater'}
                    removeFromCartText={removeFromCartText ? removeFromCartText : 'Fjern fra kurv'}
                    outOfStockText={outOfStockText ? outOfStockText : 'Ikke på lager'}
                    hasAvailableProducts={productDimensionAvailabilities?.isDisabled}
                    productAvailability={inventoryStatus?.ProductAvailableQuantity || { AvailableQuantity: 0 }}
                    data={{ cart: data.cart, product: data.simpleProduct || product1, productSearch: data.product }}
                    cartQueue={cartQueue}
                    context={context}
                    id={id}
                    disabled={disableBuyButton}
                    typeName={typeName}
                    quantity={orderQuantity}
                    breakoff={breakoff}
                    buyAlwaysAdd={buyAlwaysAdd}
                    toastStrings={toastStrings}
                    subTotalLabel={subTotalLabel}
                    goToCartButtonLabel={goToCartButtonLabel}
                    moreProductsLabel={moreProductsLabel}
                    onUpdateQuantity={(quantity: number) => {
                        if (quantity === 0) {
                            setOrderQuantity(allowZeroQuantity ? quantity : 1);
                        } else {
                            setOrderQuantity(quantity);
                        }
                    }}
                    onAdd={() => {
                        setForceUpdate(prev => prev + 1);
                    }}
                />
                {cartCount}
            </Node>
        );
    };

    const _renderOrderTemplateIndicator = (): JSX.Element | undefined => {
        let orderTemplateIndicator: JSX.Element | undefined;

        if (product.PartOfProductLists && product.PartOfProductLists.length > 0) {
            orderTemplateIndicator = (
                <div style={{ display: 'inline-block' }}>
                    <div ref={orderTemplateIndicatorRef} className={`${baseClassName}__badge order-template-indicator`}>
                        P
                    </div>
                    <UncontrolledTooltip
                        className={`${baseClassName}__order-template-indicator__tooltip`}
                        placement='bottom-start'
                        trigger='hover focus'
                        target={orderTemplateIndicatorRef}
                    >
                        <div>{/* inOrderTemplateTooltipHeader ||  */ 'Finnes i plankjøp:'}</div>
                        {product.PartOfProductLists && product.PartOfProductLists.length > 0 && (
                            <ul>
                                {product.PartOfProductLists.map((productList: IProductListByProductId) => {
                                    let url = getUrlSync('ordertemplate', context.actionContext) ?? '';
                                    const separator = url.includes('?') ? '&' : '?';

                                    if (productList.ProductListId) {
                                        url = `${url}${separator}id=${productList.ProductListId}`;
                                    }

                                    return (
                                        <li key={productList.ProductListId}>
                                            <a href={url}>{productList.Name}</a>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </UncontrolledTooltip>
                </div>
            );
        }

        return orderTemplateIndicator;
    };

    const _renderCartCount = (): JSX.Element | undefined => {
        const cartQuantity = data.cart?.cart.CartLines?.filter(x => x.ProductId === product.RecordId).reduce(
            (acc, val) => acc + (val.Quantity || 0),
            0
        );

        if (!cartQuantity) {
            return undefined;
        }

        const cartCountId = `product-component-cart-count-${product.RecordId}`;
        const tooltipString = inCartTooltipText || '{0} {1} i handlekurven';
        const humanizedUOM = humanizeUnitOfMeasure(context, product.DefaultUnitOfMeasure).toLowerCase();
        const tooltipText = format(tooltipString, cartQuantity, humanizedUOM);

        return (
            <div
                id={cartCountId}
                className={`${baseClassName}__badge cart-count-new`}
                onMouseOver={() => setShowCartCountTooltip(true)}
                onMouseOut={() => setShowCartCountTooltip(false)}
                onTouchStart={() => setShowCartCountTooltip(true)}
            >
                <span className={`${baseClassName}__cart-count-new__label`}>
                    {cartQuantity}
                    <Tooltip isOpen={showCartCountTooltip} target={`#${cartCountId}`} className='msc-cart-count-tooltip'>
                        {tooltipText}
                    </Tooltip>
                </span>
            </div>
        );
    };

    const _renderQuantityInput = (): JSX.Element => {
        return (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center'}}>
                <QuantityInput
                    context={context}
                    product={simpleProduct}
                    quantity={orderQuantity}
                    breakoff={breakoff}
                    showNonBreakoffAmount={true}
                    onQuantityChange={setOrderQuantity}
                    nonBreakoffUOM={
                        simpleProduct?.DefaultUnitOfMeasure ? humanizeUnitOfMeasure(context, simpleProduct.DefaultUnitOfMeasure) : ''
                    }
                />
            </div>
        );
    };

    const baseClassName = 'msc-product-component';
    const additionalInformation =
        (product.Brand && <div className='msc-product__title__brand'>{product.Brand}</div>) ||
        (product.Manufacturer && <div className='msc-product__title__manufacturer'>{product.Manufacturer}</div>) ||
        (product.Producer && <div className='msc-product__title__producer'>{product.Producer}</div>);
    const productLinkAttributes: React.AnchorHTMLAttributes<HTMLAnchorElement> = {
        href: productPageUrl,
        onClick: onTelemetryClick(telemetryContent!, payLoad, product.Name!),
        'aria-label': renderLabel(product.Name, product.Price.toString(), product.AverageRating, ratingAriaLabel),
        ...attribute
    }

    return (
        <div className={baseClassName}>
            {isEnabledProductDescription ? (
                <a
                    {...productLinkAttributes}
                    aria-label={renderLabel(
                        product.Name,
                        product.Price.toString(),
                        product.AverageRating,
                        ratingAriaLabel,
                        product.TotalRatings,
                        ratingCountAriaLabel
                    )}
                    className={classnames('msc-product', { 'logged-in': context.request.user.isAuthenticated })}
                >
                    <div className='msc-product__image__description'>
                        <div className='msc-product__image-container'>
                            <div className='msc-product__image'>
                                {renderProductPlacementImage(
                                    imageSettings,
                                    context.request.gridSettings,
                                    productImageUrl,
                                    product.PrimaryImageUrl,
                                    product.Name,
                                    context
                                )}
                            </div>
                        </div>
                        <div className='msc-product__title_description'>
                            <h5 className='msc-product__title__text'>{product.Name}</h5>
                            {context.request.user.isAuthenticated &&
                                renderPrice(
                                    context,
                                    typeName,
                                    id,
                                    showPrice,
                                    breakoff,
                                    product,
                                    simpleProduct,
                                    data.unitConversions,
                                    savingsText,
                                    freePriceText,
                                    originalPriceText,
                                    currentPriceText,
                                    pricePerText,
                                    isPriceMinMaxEnabled,
                                    priceResources,
                                    priceErrorText,
                                    priceText,
                                    netPriceText,
                                    discountText
                                )}
                            {renderDescription(product.Description)}
                        </div>
                    </div>
                </a>
            ) : (
                <div className={classnames('msc-product', { 'logged-in': context.request.user.isAuthenticated })}>
                    <a {...productLinkAttributes} className='msc-product__image-link'>
                        <div className='msc-product__image-container'>
                            <div className='msc-product__image'>
                                {renderProductPlacementImage(
                                    imageSettings,
                                    context.request.gridSettings,
                                    productImageUrl,
                                    product.PrimaryImageUrl,
                                    product.Name,
                                    context
                                )}
                                {product.Badge && (
                                    <DiscountBadge
                                        context={context}
                                        badge={product.Badge}
                                        size='large'
                                        sizeMultiplier={2} />
                                )}
                            </div>
                        </div>
                    </a>
                    <div className='msc-product__details'>
                        {product.Category && <div className='msc-product__category'>{product.Category}</div>}
                        <a {...productLinkAttributes} className='msc-product__title-link'>
                            <h5 className='msc-product__title' title={product.Name}>
                                <div className='msc-product__title__name'>
                                    {product.Transit && (
                                        <div className={`${baseClassName}__transit-mark`} title={transitTooltip || 'Transittvare'}>
                                            T
                                        </div>
                                    )}
                                    {showAgreementProduct && product.IsAgreementProduct && (
                                        <div className={`${baseClassName}__agreement-product`} title={agreementProductTooltip || 'Avtalevare'}>
                                            A
                                        </div>
                                    )}
                                    {product.Name}
                                </div>
                            </h5>
                        </a>
                        <div className='msc-product__sku-read-more_wrapper'>
                            <div className='msc-product__sku'>
                                {renderItemId(context, assemblyInstructionsText, product)}
                            </div>
                            <a {...productLinkAttributes} className='msc-product__read-more-link'>
                                {readMoreText || 'Read more'}
                            </a>
                        </div>
                        {additionalInformation}
                    </div>
                    {context.request.user.isAuthenticated &&
                        renderPrice(
                            context,
                            typeName,
                            id,
                            showPrice,
                            breakoff,
                            product,
                            simpleProduct,
                            data.unitConversions,
                            savingsText,
                            freePriceText,
                            originalPriceText,
                            currentPriceText,
                            pricePerText,
                            undefined,
                            undefined,
                            priceErrorText,
                            priceText,
                            netPriceText,
                            discountText
                        )}
                    {!context.request.user.isAuthenticated && (
                        <div className='msc-product__prices-not-logged-in'>{priceNotLoggedInText || 'Logg inn for å se priser'}</div>
                    )}
                </div>
            )}
            {renderProductDimensions(product.AttributeValues)}

            {context.request.user.isAuthenticated && (
                <div className='msc-product__quantity-wrapper'>
                    {_renderQuantityInput()}
                    {_renderCartButton(false)}
                </div>
            )}

            {!context.request.user.isAuthenticated && (
                <div style={{ display: 'flex', justifyContent: 'end', marginTop: 4 , marginBottom: 10 }}>
                    <a className='msc-product__login-link' href={context.request.user.signInUrl}>{signInLinkText || 'Login'}</a>
                </div>
            )}

            {!context.app.config.hideRating &&
                renderRating(context, typeName, id, product.AverageRating, product.TotalRatings, ratingAriaLabel, ratingCountAriaLabel)}

            <div className='msc-product__bottom'>
                {renderProductAvailability(context, showAvailability, inventoryLabel, inventoryStatus)}
                <div style={{ display: 'flex', gap: 4 }}>
                    {context.request.user.isAuthenticated && _renderCartCount()}
                    {context.request.user.isAuthenticated && _renderOrderTemplateIndicator()}
                </div>
            </div>

            <div className='card-actions-wrapper' tabIndex={0}>
                <div className='card-actions'>
                    {context.request.user.isAuthenticated && renderOrderTemplateButton()}
                </div>
            </div>
        </div>
    );
};

function renderLabel(
    name?: string,
    price?: string,
    rating?: number,
    ratingAriaLabelText?: string,
    reviewCount?: number,
    ratingCountAriaLabelText?: string
): string {
    const reviewCountArialableText = getReviewAriaLabel(reviewCount, ratingCountAriaLabelText ?? '');
    return `${name ?? ''} ${price ?? ''} ${getRatingAriaLabel(rating, ratingAriaLabelText)}${
        reviewCountArialableText ? ` ${reviewCountArialableText}` : ''
    }`;
}

function renderDescription(description?: string): JSX.Element | null {
    return <p className='msc-product__text'>{description}</p>;
}

function getRatingAriaLabel(rating?: number, ratingAriaLabel?: string): string {
    if (rating && ratingAriaLabel) {
        const roundedRating = rating.toFixed(2);
        return format(ratingAriaLabel || '', roundedRating, '5');
    }
    return '';
}

function getReviewAriaLabel(reviewCount?: number, ratingCountAriaLabelText?: string): string {
    if (reviewCount && ratingCountAriaLabelText) {
        return format(ratingCountAriaLabelText || '', reviewCount);
    }
    return '';
}

function renderRating(
    coreContext: ICoreContext,
    moduleTypeName: string,
    moduleId: string,
    avgRating?: number,
    totalRatings?: number,
    ariaLabel?: string,
    ratingCountAriaLabel?: string
): JSX.Element | null {
    if (!avgRating) {
        return null;
    }

    const numberRatings = totalRatings?.toString() || undefined;
    const ratingAriaLabelText = getRatingAriaLabel(avgRating, ariaLabel);
    const ratingCountAriaLabelText = getReviewAriaLabel(Number(numberRatings), ratingCountAriaLabel);

    return (
        <RatingComponent
            context={coreContext}
            id={moduleId}
            typeName={moduleTypeName}
            avgRating={avgRating}
            ratingCount={numberRatings}
            readOnly
            ariaLabel={ratingAriaLabelText}
            ratingCountAriaLabel={ratingCountAriaLabelText}
            data={{}}
        />
    );
}

function renderItemId(
    context: ICoreContext<IAppSettingsInContext>,
    assemblyInstructionsText: string | undefined,
    product: ProductSearchResultExt
): JSX.Element | null {
    const engineHeaterProductCategoryRecordId = Number(context.app.config?.engineHeaterProductCategoryRecordId);
    const categorySegments = product.CategoryPaths?.[0]?.Segments;
    const productCategory = categorySegments?.length ? categorySegments[categorySegments.length - 1] : undefined;

    // If product is in the engine heater category, we render the item id with a link to assembly instructions
    if (engineHeaterProductCategoryRecordId && productCategory?.CategoryId === engineHeaterProductCategoryRecordId) {
        const assemblyManualUrl = `https://www.defa.com/content/uploads/Documentation/Enginge-heaters/Find-engine-heater/Manuals-and-pdfs/${product.ItemId}.pdf`;

        return (
            <a
                className='msc-product__sku__item-id-link'
                href={assemblyManualUrl}
                target='_blank'
                rel='noopener noreferrer'
                title={assemblyInstructionsText || 'Assembly Instructions'}
            >
                <span>
                    {product.ItemId}
                </span>
            </a>
        );
    }

    return <span className='msc-product__sku__item-id'>{product.ItemId}</span>;
}

function renderPrice(
    context: ICoreContext,
    typeName: string,
    id: string,
    show: boolean,
    breakoff: boolean,
    product?: ProductSearchResultExt,
    simpleProduct?: SimpleProductExt,
    unitConversions?: UnitOfMeasureConversion[] | undefined,
    savingsText?: string,
    freePriceText?: string,
    originalPriceText?: string,
    currentPriceText?: string,
    pricePerText?: string,
    isPriceMinMaxEnabled?: boolean,
    priceResources?: IPriceComponentResources,
    priceErrorText?: string,
    priceText?: string,
    netPriceText?: string,
    discountText?: string
): JSX.Element | null {
    const price: ProductPrice = {
        BasePrice: product?.BasePrice,
        AdjustedPrice: product?.Price,
        CustomerContextualPrice: product?.Price
    };

    if (product?.PriceFailed) {
        return (
            <div className='msc-product__prices'>
                <span className='msc-price'>
                    <span className='msc-price__actual error'>{priceErrorText || 'Pris n/a'}</span>
                </span>
            </div>
        );
    }

    if (!show || !simpleProduct) {
        return (
            <div className='msc-product__prices'>
                <span className='msc-price'>
                    <span className='msc-price__actual retrieving'>...</span>
                </span>
            </div>
        );
    }

    const productPrice = { ...(product?.ProductPrice || price) };

    return (
        <div className='msc-product__prices'>
            <div>
                <PriceComponent
                    context={context}
                    id={id}
                    className='price'
                    typeName={typeName}
                    data={{ price: productPrice }}
                    savingsText={savingsText}
                    freePriceText={freePriceText}
                    originalPriceText={originalPriceText}
                    isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                    priceResources={priceResources}
                    priceText={priceText}
                    netPriceText={netPriceText}
                    discountText={discountText}
                    showAsGrid
                />
            </div>
            {/* <div>
                <div
                    className={classnames('per-price__wrapper', {
                        hidden: breakoff || !simpleProduct?.Behavior?.MultipleOfQuantity
                    })}
                >
                    <PriceComponent
                        context={context}
                        id={id}
                        className='per-price'
                        typeName={typeName}
                        data={{
                            price: {
                                CustomerContextualPrice:
                                    product?.ProductPrice?.CustomerContextualPrice || price.CustomerContextualPrice || 0
                            }
                        }}
                        savingsText={savingsText}
                        freePriceText={freePriceText}
                        originalPriceText={originalPriceText}
                        isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                        priceResources={priceResources}
                    />
                    <span className='price-suffix'>
                        &nbsp;
                        {format(
                            pricePerText || 'pr. {0}',
                            humanizeUnitOfMeasure(context, simpleProduct.DefaultUnitOfMeasure, simpleProduct).toLocaleLowerCase()
                        )}
                    </span>
                </div>
            </div> */}
        </div>
    );
}

function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    context?: ICoreContext<IGeneric<IAny>>
): JSX.Element | null {
    if (!gridSettings || !imageSettings) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
    const contextConfig = context?.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = context?.request.apiSettings.baseImageUrl;
    if (fallbackImageSource?.endsWith('/')) {
        fallbackImageSource = fallbackImageSource.slice(0, -1);
    }
    if (emptyPlaceHolderImage) {
        fallbackImageSource = `${fallbackImageSource}${emptyPlaceHolderImage}`;
    }
    // let imageUrl2 = ensureBaseImageUrl(context as ICoreContext, imageUrl);
    // let fallbackImageUrl2 = ensureBaseImageUrl(context as ICoreContext, fallbackImageUrl);
    const img: IImageData = {
        src: imageUrl || fallbackImageSource,
        altText: altText ? altText : '',
        fallBackSrc: fallbackImageSource
    };
    const imageProps = {
        gridSettings,
        imageSettings
    };
    imageProps.imageSettings.cropFocalRegion = true;
    imageProps.imageSettings.mode = 0;
    const w = 240;
    const h = 240;
    const sharedViewports = {
        h,
        w,
        q: `w=${w}&h=${h}&q=80&m=0&f=webp`,
        qe: `t=${w}h${h}qhigh`
    };
    imageProps.imageSettings.viewports = {
        xs: { ...sharedViewports },
        lg: { ...sharedViewports },
        md: { ...sharedViewports },
        sm: { ...sharedViewports },
        xl: { ...sharedViewports }
    };

    return (
        <Image
            requestContext={context?.actionContext.requestContext}
            {...img}
            {...imageProps}
            loadFailureBehavior='empty'
            bypassHideOnFailure
        />
    );
}

export const ProductComponent: React.FunctionComponent<IProductComponentProps> = msdyn365Commerce.createComponentOverride<
    IProductComponent
>('Product', { component: ProductCard, ...PriceComponentActions });

export default ProductComponent;
